import { IconPlus } from '@tabler/icons-react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  type ClientFilters,
  type ClientSortBy,
  type ClientsResponse,
  getClients,
  isClientSortBy,
} from '@/api/clients';
import { ClientQuickViewModal } from '@/components/client-quick-view-modal';
import { type ClientRow, ClientsTable } from '@/components/clients-table';
import type { DataTableResponse } from '@/components/data-table';
import { NewClientModal } from '@/components/modals/new-client-modal';
import { SearchInput } from '@/components/search-input';
import { Button } from '@/components/ui/button';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';
import { useFocusReturn } from '@/hooks/use-focus-return';
import { isInternalAdminUser, isInternalUser } from '@/lib/authz';

/**
 * The API rejects an unknown `sortBy` with a 400, which the grid can only show
 * as an error state -- so a stale or hand-edited URL falls back instead.
 */
function readClientSortBy(raw: string | null, fallback: ClientSortBy): ClientSortBy {
  const value = raw?.trim() ?? '';
  return isClientSortBy(value) ? value : fallback;
}

function buildClientsSearchParams(filters: ClientFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.page && filters.page !== 1) params.set('page', String(filters.page));
  if (filters.limit && filters.limit !== 10) params.set('limit', String(filters.limit));
  if (filters.sortBy && filters.sortBy !== 'created_at') params.set('sortBy', filters.sortBy);
  if (filters.order && filters.order !== 'desc') params.set('sortOrder', filters.order);
  if (filters.status) params.set('status', filters.status);
  if (filters.search) params.set('search', filters.search);
  return params;
}

export const Clients: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading, isInitialized, user } = useKeycloakAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastWrittenQueryRef = useRef<string | null>(null);
  const [clientsData, setClientsData] = useState<ClientsResponse | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);
  const initialSearch = searchParams.get('search')?.trim() || '';
  const [filters, setFilters] = useState<ClientFilters>(() => {
    const initial: ClientFilters = {
      page: Number(searchParams.get('page') || 1) || 1,
      limit: Number(searchParams.get('limit') || 10) || 10,
      sortBy: readClientSortBy(searchParams.get('sortBy'), 'created_at'),
      order: ((searchParams.get('sortOrder') || 'desc').toLowerCase() as 'asc' | 'desc') || 'desc',
    };
    const status = searchParams.get('status')?.trim();
    if (status) initial.status = status;
    if (initialSearch) initial.search = initialSearch;
    return initial;
  });
  const [search, setSearch] = useState(initialSearch);

  const [isNewClientModalOpen, setIsNewClientModalOpen] = useState(false);
  const [quickViewClient, setQuickViewClient] = useState<ClientRow | null>(null);
  const { capture: captureFocus, restore: restoreFocus } = useFocusReturn();

  const hasInternalAccess = isInitialized && isAuthenticated && isInternalUser(user?.roles);
  // Client creation is an admin-tier action; viewing/stats are available to any internal user.
  const canManageClients = isInitialized && isAuthenticated && isInternalAdminUser(user?.roles);

  const getClientLink = useCallback((client: ClientRow) => ({ to: `/clients/${client.id}` }), []);

  const handleQuickView = useCallback(
    (client: ClientRow) => {
      captureFocus();
      setQuickViewClient(client);
    },
    [captureFocus]
  );

  const fetchClients = useCallback(async (currentFilters: ClientFilters) => {
    const isInitialLoad = !hasLoadedRef.current;
    try {
      // Only show loading spinner if we don't have data yet (initial load)
      if (isInitialLoad) {
        setIsLoading(true);
      }
      setError(null);
      const data = await getClients(currentFilters);
      setClientsData(data);
      hasLoadedRef.current = true;
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to load clients');
    } finally {
      if (isInitialLoad) {
        setIsLoading(false);
      }
    }
  }, []);

  const handleNewClientSuccess = useCallback(() => {
    setIsNewClientModalOpen(false);
    toast.success('Client created successfully');
    fetchClients(filters);
  }, [fetchClients, filters]);

  const handleFilterChange = useCallback((filterKey: string, value: string | string[]) => {
    setFilters((prev) => ({
      ...prev,
      page: 1,
      [filterKey]: Array.isArray(value) ? value[0] || undefined : value || undefined,
    }));
  }, []);

  const handleSortChange = useCallback((sortBy: string, order: 'asc' | 'desc') => {
    // Guard instead of cast: sending a key the API can't sort by 400s the whole
    // list, so an unsupported column leaves the current ordering alone.
    if (!isClientSortBy(sortBy)) return;
    setFilters((prev) => ({
      ...prev,
      sortBy,
      order,
    }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({
      ...prev,
      page,
    }));
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      setFilters((prev) => {
        const prevSearch = (prev as ClientFilters & { search?: string }).search;
        const nextSearch = search === '' ? undefined : search;
        if (prevSearch === nextSearch) {
          return prev;
        }
        const base = { ...prev, page: 1 } as ClientFilters;
        if (nextSearch === undefined) {
          const { search: _omitted, ...rest } = base as ClientFilters & { search?: string };
          return rest;
        }
        return { ...base, search: nextSearch };
      });
    }, 300);
    return () => clearTimeout(debounce);
  }, [search]);

  // Sync URL -> state (supports back/forward and shared links)
  useEffect(() => {
    const current = searchParams.toString();
    if (lastWrittenQueryRef.current && lastWrittenQueryRef.current === current) {
      lastWrittenQueryRef.current = null;
      return;
    }

    const nextSearch = searchParams.get('search')?.trim() || '';
    setSearch(nextSearch);

    setFilters((prev) => {
      const next: ClientFilters = {
        ...prev,
        page: Number(searchParams.get('page') || 1) || 1,
        limit: Number(searchParams.get('limit') || prev.limit || 10) || prev.limit || 10,
        sortBy: readClientSortBy(searchParams.get('sortBy'), prev.sortBy ?? 'created_at'),
        order:
          ((searchParams.get('sortOrder') || prev.order || 'desc').toLowerCase() as
            | 'asc'
            | 'desc') || 'desc',
      };

      const status = searchParams.get('status')?.trim();
      if (status) {
        next.status = status;
      } else {
        delete next.status;
      }

      if (nextSearch) {
        next.search = nextSearch;
      } else {
        delete next.search;
      }

      return next;
    });
  }, [searchParams]);

  // Sync state -> URL (so refresh/share preserves the current view)
  useEffect(() => {
    const nextParams = buildClientsSearchParams(filters);
    const next = nextParams.toString();
    const current = searchParams.toString();
    if (next === current) return;
    lastWrittenQueryRef.current = next;
    setSearchParams(nextParams, { replace: true });
  }, [filters, searchParams, setSearchParams]);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchClients(filters);
    } else if (!authLoading && !isAuthenticated) {
      setError('Please log in to view clients');
      setIsLoading(false);
    }
  }, [filters, fetchClients, isAuthenticated, authLoading]);

  const tableData: DataTableResponse<ClientRow> | undefined = clientsData
    ? {
        data: clientsData.data.map((c) => ({
          id: c.id,
          name: c.name,
          clientCode: c.client_code,
          email: c.company_email,
          status: c.status,
          createdAt: c.created_at.toString(),
          primaryContactName: c.primary_contact_name ?? null,
          primaryContactPhone: c.primary_contact_phone ?? null,
        })),
        pagination: {
          page: clientsData.pagination.page,
          pageSize: clientsData.pagination.limit,
          total: clientsData.pagination.total,
          totalPages: clientsData.pagination.totalPages,
        },
      }
    : undefined;

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
      <div className="px-4 lg:px-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">Clients</h1>
          </div>
          {canManageClients && (
            <Button
              onClick={() => setIsNewClientModalOpen(true)}
              className="gap-2"
              disabled={!isAuthenticated || authLoading}
            >
              <IconPlus className="h-4 w-4" />
              New Client
            </Button>
          )}
        </div>
      </div>

      <div className="px-4 lg:px-6">
        <ClientsTable
          key={`clients-table-${hasInternalAccess}`}
          data={tableData}
          isLoading={isLoading}
          error={error}
          getRowLink={hasInternalAccess ? getClientLink : undefined}
          onQuickView={hasInternalAccess ? handleQuickView : undefined}
          onFilterChange={handleFilterChange}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
          currentSortKey={filters.sortBy ?? 'created_at'}
          currentSortOrder={filters.order ?? 'desc'}
          searchInput={
            <SearchInput
              id="clients-search"
              value={search}
              onChange={setSearch}
              placeholder="Search clients..."
            />
          }
        />
      </div>

      {canManageClients && (
        <NewClientModal
          open={isNewClientModalOpen}
          onClose={() => setIsNewClientModalOpen(false)}
          onSuccess={handleNewClientSuccess}
        />
      )}

      {hasInternalAccess && (
        <ClientQuickViewModal
          open={quickViewClient !== null}
          onClose={() => {
            restoreFocus();
            setQuickViewClient(null);
          }}
          client={quickViewClient}
        />
      )}
    </div>
  );
};
